// Plain-language phrasing for the map's numbers, for non-technical readers while
// staying honest. Two kinds of band travel through the same files (W5, ship note
// docs/prod/SHIP_DECISION_7_3.md section 9c): the legacy model's probability
// cutoffs and the rebuilt model's fixed territory shares. Every page keys its
// wording on the served model family so a rollback to the legacy path (W7) keeps
// the words true without a redeploy.

import type { RiskLevel } from '../types';

export type BandKind = 'share' | 'probability';

/** Model families whose bands are fixed shares of the territory (mirrors
 * FIXED_SHARE_MODEL_PREFIXES in scripts/bake_validacao.py). */
const SHARE_MODEL_PREFIXES = ['offset-booster:', 'lookup:'];

export function bandKindOf(modelVersion: string | null | undefined): BandKind {
  const v = (modelVersion ?? '').toLowerCase();
  return SHARE_MODEL_PREFIXES.some((p) => v.startsWith(p)) ? 'share' : 'probability';
}

/** Share of the territory each band takes, per day, by construction (decision D3). */
export const BAND_SHARE_PCT: Record<RiskLevel, number> = {
  Extreme: 1,
  High: 2,
  Moderate: 5,
  Low: 92,
};

/** The three upper bands together: the area the map flags every day. */
export const FLAGGED_SHARE_PCT = BAND_SHARE_PCT.Extreme + BAND_SHARE_PCT.High + BAND_SHARE_PCT.Moderate;

/** Legend text per band in share mode (decision D7, O2). */
export const BAND_SHARE_LABEL: Record<RiskLevel, string> = {
  Extreme: '1% do território com maior risco',
  High: '2% seguintes',
  Moderate: '5% seguintes',
  Low: 'restantes 92%',
};

/** One line under the legend, share mode (decision D7). */
export const SHARE_CAVEAT = 'O mapa ordena o território; não é a probabilidade de arder.';

/** Published 2025 ignition rate of each band, percent of cell-days (decision D8,
 * O3; ship note section 5). The level runs about 12 percent low on 2025 and 2026
 * (expected over observed 0.88), which the Validação page measures. */
export const BAND_IGNITION_RATE_2025_PCT: Record<RiskLevel, number> = {
  Extreme: 0.3,
  High: 0.15,
  Moderate: 0.08,
  Low: 0.01,
};

function fmtPt(n: number, digits: number): string {
  return n.toFixed(digits).replace('.', ',');
}

/** "Em 2025, 0,30% das quadrículas-dia desta classe tiveram uma ignição (cerca de 1 em 333)." */
export function bandRateText(level: RiskLevel): string {
  const rate = BAND_IGNITION_RATE_2025_PCT[level];
  const oneIn = Math.round(100 / rate);
  return (
    `Em 2025, ${fmtPt(rate, 2)}% das quadrículas-dia desta classe tiveram uma ignição ` +
    `(cerca de 1 em ${oneIn.toLocaleString('pt-PT')}).`
  );
}

/** Whether a cells array carries the legacy seasonal percentile (cell[4]). The
 * daily path publishes null there, so the anomaly views hide themselves. */
export function hasSeasonalPercentile(cells: number[][] | null | undefined): boolean {
  if (!cells) return false;
  return cells.some((c) => c[4] != null);
}

/** "10 out 2026, 06:16" in the reader's clock, for the issue time (decision D5). */
export function fmtIssued(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export interface SeasonalComparison {
  label: string;
  detail: string | null;
  anomaly: boolean;
}

export function seasonalComparison(
  percentile: number | null | undefined,
): SeasonalComparison | null {
  if (percentile == null) return null;
  const p = Math.round(percentile);
  if (p >= 95) {
    return {
      label: 'Invulgarmente perigoso para a época',
      detail: `Nos ${Math.max(1, 100 - p)}% de dias mais perigosos desta altura do ano`,
      anomaly: true,
    };
  }
  if (p >= 75) {
    return {
      label: 'Bastante acima do normal para a época',
      detail: `Mais perigoso que ${p}% dos dias típicos desta altura do ano`,
      anomaly: false,
    };
  }
  if (p >= 50) {
    return {
      label: 'Acima do normal para a época',
      detail: `Mais perigoso que ${p}% dos dias típicos desta altura do ano`,
      anomaly: false,
    };
  }
  return { label: 'Dentro do normal para a época', detail: null, anomaly: false };
}
